import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Users, Plus, ChevronLeft, Loader2,
  ShieldCheck, ShieldOff, Wallet, AlertCircle,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/api/apiClient';
import { useOdooAuth } from '@/lib/OdooAuthContext';

/**
 * Manager-only screen that lists every SellGo rep with a quick
 * snapshot of their key settings. Clicking a row opens the per-rep
 * editor at `/admin/reps/:id`.
 *
 * Anyone without manager privileges is bounced to the dashboard.
 */
export default function AdminReps() {
  const navigate = useNavigate();
  const { odooSession } = useOdooAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddPicker, setShowAddPicker] = useState(false);

  // Guard: managers only.
  useEffect(() => {
    if (odooSession && odooSession.role !== 'manager') {
      navigate('/', { replace: true });
    }
  }, [odooSession, navigate]);

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const res = await api.get('/sellgo_profiles_list', { limit: 200 });
      if (res?.success) setProfiles(res.data || []);
      else toast.error(res?.error || 'فشل تحميل المناديب');
    } catch (e) {
      toast.error(e?.message || 'حدث خطأ');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadProfiles();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return profiles;
    const q = search.trim().toLowerCase();
    return profiles.filter(p => {
      const name = (p.user_id?.[1] || '').toLowerCase();
      const login = (p.user_login || '').toLowerCase();
      const email = (p.user_email || '').toLowerCase();
      return name.includes(q) || login.includes(q) || email.includes(q);
    });
  }, [profiles, search]);

  return (
    <div className="flex flex-col min-h-full" dir="rtl">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-xl bg-muted hover:bg-muted/70 flex items-center justify-center"
        >
          <ChevronLeft className="w-5 h-5 rotate-180" />
        </button>
        <div className="flex-1">
          <h1 className="font-black text-foreground text-lg">إدارة المناديب</h1>
          <p className="text-xs text-muted-foreground">
            صلاحيات، حدود، طرق الدفع، المخازن والفئات
          </p>
        </div>
        <Button
          onClick={() => setShowAddPicker(true)}
          className="rounded-xl gap-2 bg-primary hover:bg-primary/90"
        >
          <Plus className="w-4 h-4" />
          مندوب جديد
        </Button>
      </div>

      {/* Search */}
      <div className="px-4 pt-4">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="بحث بالاسم أو البريد..."
            className="pr-11 h-11 bg-card rounded-2xl text-base border-2 focus:border-primary/40"
          />
        </div>
      </div>

      {/* Stats strip */}
      {!loading && profiles.length > 0 && (
        <div className="px-4 pt-3 grid grid-cols-3 gap-2">
          <StatChip
            label="إجمالي"
            value={profiles.length}
            icon={<Users className="w-4 h-4" />}
            color="primary"
          />
          <StatChip
            label="نشط"
            value={profiles.filter(p => p.active).length}
            icon={<ShieldCheck className="w-4 h-4" />}
            color="emerald"
          />
          <StatChip
            label="موقوف"
            value={profiles.filter(p => !p.active).length}
            icon={<ShieldOff className="w-4 h-4" />}
            color="red"
          />
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center">
              <Users className="w-8 h-8 text-muted-foreground/60" />
            </div>
            <p className="text-sm font-medium text-foreground">
              {search ? 'لا يوجد مندوب مطابق' : 'لا يوجد مناديب بعد'}
            </p>
            {!search && (
              <Button
                onClick={() => setShowAddPicker(true)}
                variant="outline"
                className="rounded-xl gap-2 mt-2"
              >
                <Plus className="w-4 h-4" /> أضف مندوب
              </Button>
            )}
          </div>
        ) : (
          filtered.map(profile => (
            <RepProfileCard
              key={profile.id}
              profile={profile}
              onClick={() => navigate(`/admin/reps/${profile.id}`)}
            />
          ))
        )}
      </div>

      {/* Add-rep picker overlay */}
      {showAddPicker && (
        <AddRepPicker
          onClose={() => setShowAddPicker(false)}
          onAdded={(newProfile) => {
            setShowAddPicker(false);
            setProfiles(prev => [newProfile, ...prev]);
            navigate(`/admin/reps/${newProfile.id}`);
          }}
        />
      )}
    </div>
  );
}

function StatChip({ label, value, icon, color }) {
  const palette = {
    primary: 'bg-primary/10 text-primary border-primary/20',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <div className={`border rounded-2xl p-3 ${palette[color] || palette.primary}`}>
      <div className="flex items-center gap-1.5 text-xs font-bold opacity-80">
        {icon} {label}
      </div>
      <p className="text-2xl font-black mt-1">{value}</p>
    </div>
  );
}

function RepProfileCard({ profile, onClick }) {
  const name = profile.user_id?.[1] || profile.user_login || 'بدون اسم';
  const email = profile.user_email || profile.user_login || '';

  // Quick badges that signal the most important policy bits at a glance
  const cashOnly = profile.allow_cash
    && !profile.allow_check
    && !profile.allow_bank_transfer
    && !profile.allow_wallet;
  const noLimit = !profile.order_confirm_limit;
  const wareCount = profile.warehouse_ids?.length || 0;
  const catCount = profile.dealer_category_ids?.length || 0;

  return (
    <button
      onClick={onClick}
      className={`w-full text-right border rounded-2xl p-3.5 flex items-center gap-3 transition-all hover:shadow-sm hover:border-primary/40 ${
        profile.active
          ? 'bg-card border-border'
          : 'bg-muted/40 border-muted opacity-70'
      }`}
    >
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center flex-shrink-0">
        <span className="text-primary font-black text-lg">{name.charAt(0)}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-bold text-foreground truncate">{name}</p>
          {!profile.active && (
            <span className="text-[10px] font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-md">
              موقوف
            </span>
          )}
          {profile.is_manager && (
            <span className="text-[10px] font-bold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-md">
              مدير
            </span>
          )}
        </div>
        {email && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">{email}</p>
        )}
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          {cashOnly && (
            <Tag color="amber" icon={<Wallet className="w-3 h-3" />}>نقدي فقط</Tag>
          )}
          {!noLimit && (
            <Tag color="blue">
              سقف {Number(profile.order_confirm_limit).toLocaleString('ar-EG')}
            </Tag>
          )}
          {noLimit && profile.allow_confirm_order && (
            <Tag color="red" icon={<AlertCircle className="w-3 h-3" />}>بدون حد</Tag>
          )}
          {wareCount > 0 && <Tag color="slate">{wareCount} مخزن</Tag>}
          {catCount > 0 && <Tag color="slate">{catCount} فئة</Tag>}
        </div>
      </div>
      <ChevronLeft className="w-5 h-5 text-muted-foreground/50 flex-shrink-0" />
    </button>
  );
}

function Tag({ color, icon, children }) {
  const palette = {
    amber: 'bg-amber-50 text-amber-700',
    blue: 'bg-blue-50 text-blue-700',
    red: 'bg-red-50 text-red-700',
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-50 text-emerald-700',
  };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md inline-flex items-center gap-1 ${palette[color] || palette.slate}`}>
      {icon}{children}
    </span>
  );
}

function AddRepPicker({ onClose, onAdded }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  const loadUsers = async (q = '') => {
    setLoading(true);
    const res = await api.get('/sellgo_admin_users', { search: q || undefined });
    if (res?.success) setUsers(res.data || []);
    setLoading(false);
  };

  useEffect(() => { loadUsers(''); }, []);

  const handleSearch = (v) => {
    setSearch(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => loadUsers(v), 250);
  };

  const pickUser = async (u) => {
    setSaving(true);
    try {
      const res = await api.post('/sellgo_profile_save', { user_id: u.id });
      if (res?.success) {
        toast.success(`تم إنشاء ملف لـ ${u.name}`);
        onAdded(res.data);
      } else {
        toast.error(res?.error || 'فشل الإنشاء');
      }
    } catch (e) {
      toast.error(e?.message || 'حدث خطأ');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end" onClick={onClose}>
      <div
        className="bg-card w-full rounded-t-3xl p-5 space-y-4 max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-black text-foreground text-lg">اختيار مستخدم</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="بحث..."
            className="pr-10 bg-muted/50 rounded-xl"
            autoFocus
          />
        </div>
        <div className="flex-1 overflow-y-auto space-y-2">
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-6">
              لا يوجد مستخدم مطابق
            </p>
          ) : (
            users.map(u => (
              <button
                key={u.id}
                onClick={() => !saving && pickUser(u)}
                disabled={saving}
                className="w-full text-right p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors flex items-center gap-3 disabled:opacity-50"
              >
                <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
                  <span className="text-primary font-bold">{u.name?.charAt(0) || '?'}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{u.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {u.email || u.login}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
